import { ListLoading } from '@/components/ui';
import { Link } from 'react-router';
import { ProjectDot } from '@/components/ProjectDot';
import { isWork, useDaySessions, useProjects } from '@/data/hooks';
import { groupSum, sumSec } from '@/data/stats';
import { projectColor } from '@/lib/projectColors';
import { formatDuration } from '@/lib/time';

/**
 * Right column (4a/8a): a hairline stacked bar over rows of today's time per project. No card - it sits directly on
 * the canvas so the ring stays the only heavy thing on screen.
 */
export function TodayByProject({ now }: { now: Date }) {
  const today = useDaySessions(now);
  const sessions = (today ?? []).filter(isWork);
  const projects = useProjects();
  const byProject = groupSum(sessions, (s) => s.projectId);
  const rows = [
    ...projects.filter((p) => byProject.has(p.id)).map((p) => ({ key: p.id, label: p.name, value: byProject.get(p.id)!, color: projectColor(p), project: p })),
    ...(byProject.has(null) ? [{ key: 'none', label: 'No project', value: byProject.get(null)!, color: 'var(--color-empty-slot)', project: null }] : []),
  ].sort((a, b) => b.value - a.value);
  const total = sumSec(sessions);

  return (
    <div className="w-full lg:w-[290px] lg:justify-self-end flex flex-col gap-[14px] lg:self-start lg:mt-[34px]">
      <div className="flex items-baseline justify-between">
        <p className="text-eyebrow text-ink-2 m-0">Today by project</p>
        <Link to="/projects" className="text-[11px] font-bold text-accent-text no-underline hover:underline">Projects →</Link>
      </div>
      {!today ? (
        <ListLoading className="min-h-[60px]" />
      ) : rows.length === 0 ? (
        <p className="text-body-sm text-ink-2 m-0">Nothing logged today yet. Press Start and it shows up here.</p>
      ) : (
        <>
          {total > 0 && (
            <div className="flex h-[6px] gap-[2px] w-full" role="img" aria-label={`Today: ${rows.map((r) => `${r.label} ${formatDuration(r.value, { zero: '0m' })}`).join(', ')}`}>
              {rows.map((r) => <span key={r.key} className="rounded-[3px]" style={{ flex: r.value, background: r.color }} />)}
            </div>
          )}
          <div className="flex flex-col">
            {rows.map((r) => (
              <div key={r.key} className="flex items-center gap-[10px] py-[9px] border-b border-ring-track text-[13px]">
                <ProjectDot project={r.project} />
                <span className="flex-1 font-bold text-ink truncate">{r.label}</span>
                <span className="font-mono text-[12px] font-semibold text-ink">{formatDuration(r.value, { zero: '0m' })}</span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-[10px] text-[12px] font-bold text-ink-2">
              <span>Total</span><span className="font-mono text-ink">{formatDuration(total, { zero: '0m' })}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
